import React, { useEffect, useState } from 'react';

import { styled } from '@mui/material/styles';
import {
    Card,
    CardContent,
    Container,
    Divider,
    Stack,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { HiddenComponent } from 'common/components';
import { getIsGuess } from 'common/utils/helpers/session';

import Login1 from 'resources/login/login-1.svg?react';
import LoginBackground from 'resources/login/loginBackground.png';
import Logo from 'resources/logotipo.svg';

import * as actions from './actions';
import FirstStepAuthForm from './components/FirstStepAuthForm';
import SecondStepAuthForm from './components/SecondStepAuthForm';
import * as selectors from './selectors';

const StyledContainer = styled(Container)(() => ({
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
}));

const StyledCard = styled(Card)(({ theme, backgroundimg }) => ({
    display: 'flex',
    minHeight: '90%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 44px',
    width: 'inherit',
    [theme.breakpoints.down('md')]: {
        backgroundImage: `url(${backgroundimg})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top center',
        backgroundSize: 'contain',
        boxShadow: 'none',
    },
}));

const MainContainer = ({
    errorsInitAuth,
    errorsSession,
    formValues,
    initAuth,
    initialValues,
    initResendCode,
    isLoadingInitAuth,
    isLoadingSession,
    isResendingCode,
    loginUser,
    redirect,
    showSecondStepAuthForm,
    updateTooltipState,
    validPassword,
}) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (!getIsGuess()) redirect('/');
    }, []);

    const handleShowPassword = () => {
        setShowPassword((show) => !show);
    };

    const handleSubmitInitAuth = (values) => initAuth(values);
    const handleResendCode = (values) => initResendCode(values);
    const handleSubmitInitSession = (values) =>
        loginUser(values, () => redirect('/companies'));

    const onChangePassword = (event) => updateTooltipState(event);

    return (
        <Stack
            direction="row"
            sx={{
                display: 'flex',
                minHeight: '100%',
            }}
        >
            <StyledContainer backgroundcolor="#fff" maxWidth={false}>
                <StyledCard backgroundimg={LoginBackground}>
                    <CardContent
                        sx={{
                            width: '100%',
                        }}
                    >
                        <Divider textAlign="left">
                            <img loading="lazy" src={Logo} width="533px" />
                        </Divider>
                        <Stack sx={{ mb: 5, mt: 5 }}>
                            <Typography variant="h5">
                                {showSecondStepAuthForm
                                    ? t('Validate')
                                    : t('Log in')}
                                -
                            </Typography>
                            <Typography sx={{ color: 'text.secondary' }}>
                                {showSecondStepAuthForm
                                    ? t(
                                          'We send you a temporary verification code to the email associated with your account',
                                      )
                                    : t(
                                          'Identify yourself as an user with your email',
                                      )}
                            </Typography>
                        </Stack>
                        {showSecondStepAuthForm ? (
                            <SecondStepAuthForm
                                errorsSession={errorsSession}
                                formValues={formValues}
                                handleResendCode={handleResendCode}
                                handleSubmitInitSession={
                                    handleSubmitInitSession
                                }
                                initialValues={initialValues}
                                isLoadingSession={isLoadingSession}
                                isResendingCode={isResendingCode}
                                redirect={redirect}
                            />
                        ) : (
                            <FirstStepAuthForm
                                errorsInitAuth={errorsInitAuth}
                                handleShowPassword={handleShowPassword}
                                handleSubmitInitAuth={handleSubmitInitAuth}
                                initialValues={initialValues}
                                isLoadingInitAuth={isLoadingInitAuth}
                                onChangePassword={onChangePassword}
                                showPassword={showPassword}
                                validPassword={validPassword}
                            />
                        )}
                    </CardContent>
                </StyledCard>
            </StyledContainer>
            <HiddenComponent width="mdDown">
                <StyledContainer
                    backgroundcolor="#E1EFFF"
                    backgroundimg={LoginBackground}
                    sx={{
                        '& svg': {
                            color: 'primary.main',
                            maxHeight: '600px',
                            maxWidth: '100%',
                        },
                    }}
                >
                    <Login1 />
                </StyledContainer>
            </HiddenComponent>
        </Stack>
    );
};

MainContainer.propTypes = {
    errorsInitAuth: PropTypes.array,
    errorsSession: PropTypes.array,
    formValues: PropTypes.object,
    initAuth: PropTypes.func,
    initialValues: PropTypes.object,
    initResendCode: PropTypes.func,
    isLoadingInitAuth: PropTypes.bool,
    isLoadingSession: PropTypes.bool,
    isResendingCode: PropTypes.bool,
    loginUser: PropTypes.func,
    redirect: PropTypes.func,
    showSecondStepAuthForm: PropTypes.bool,
    updateTooltipState: PropTypes.func,
    validPassword: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
    errorsInitAuth: selectors.getErrorsInitAuth,
    errorsSession: selectors.getErrorsSession,
    formValues: selectors.getValues,
    initialValues: selectors.getInitialValues,
    isLoadingInitAuth: selectors.getIsLoadingInitAuth,
    isLoadingSession: selectors.getIsLoadingSession,
    isResendingCode: selectors.getIsLoadingResendCode,
    showSecondStepAuthForm: selectors.getIsShowSecondStepAuthForm,
    validPassword: selectors.getValidPassword,
});

const mapDispatchToProps = (dispatch) => ({
    initAuth: (data) => dispatch(actions.preAuth(data)),
    initResendCode: (data) => dispatch(actions.resendCode(data)),
    loginUser: (data, callback) => dispatch(actions.auth(data, callback)),
    redirect: (link) => dispatch(push(link)),
    updateTooltipState: (event) => dispatch(actions.updateTooltipState(event)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MainContainer);
